import API from "../../utils/api";
import { createAsyncThunk } from "@reduxjs/toolkit";
import FileSaver from "file-saver";
import { Toaster } from "../../utils/common";
import {
  isEndpointNotImplementedError,
  isTabUnreadCountsApiUnavailable,
  markTabUnreadCountsApiUnavailable,
} from "../../utils/documentApiCapability";
import { parseGetUserDocsResponse } from "../../utils/documentTabUnread";

const getUserFiles = createAsyncThunk(
  "document/getUserDocsStatus",
  async (state) => {
    try {
      const response = await API.post(`document/getUserDocs`, state);
      return parseGetUserDocsResponse(response);
    } catch (error) {
      return { files: [], unread_counts: null, by_folder: null };
    }
  }
);

const getUserFolders = createAsyncThunk(
  "folder/getUserFolderStatus",
  async (state) => {
    const {
      user_id,
      folder_name,
      folder_id,
      document_category,
      client_business_id,
      home_folder,
    } = state;
    try {
      const params = new URLSearchParams();
      if (document_category) {
        params.set("document_category", document_category);
      }
      if (client_business_id != null && client_business_id !== "") {
        params.set("client_business_id", String(client_business_id));
      }
      if (home_folder) {
        params.set("home_folder", String(home_folder));
      }
      const qs = params.toString() ? `?${params.toString()}` : "";
      const safeFolderName = encodeURIComponent(String(folder_name ?? ""));
      const safeFolderId = encodeURIComponent(String(folder_id ?? ""));
      const {
        data: { data },
      } = await API.get(
        `folder/getUserFolders/${user_id}/${safeFolderName}/${safeFolderId}${qs}`
      );
      return data;
    } catch (error) {
      console.log(error);
      return [];
    }
  }
);

// Fetch all documents for a particular client/user (used for Downloaded section
// and the e-filing signed consents picker). Optional `document_category` filter.
const getAllClientDocuments = createAsyncThunk(
  "document/getAllClientDocumentsStatus",
  async (payload) => {
    try {
      const { user_id, client_id, document_category, client_business_id } =
        payload || {};
      const effectiveId = user_id || client_id;
      const body = {
        user_id: effectiveId,
        client_id: effectiveId,
      };
      if (document_category) body.document_category = document_category;
      if (client_business_id != null && client_business_id !== "") {
        body.client_business_id = client_business_id;
      }
      const {
        data: { data },
      } = await API.post(`document/getAllClientDocuments`, body);
      return data || [];
    } catch (error) {
      return [];
    }
  },
);

const getFolderDetails = async (folder_id) => {
  try {
    const data = await API.get(`folder/getFolderDetails/${folder_id}`);
    return data;
  } catch (error) {
    console.log(error);
    return [];
  }
};

const getDocumentDetails = async (document_id) => {
  try {
    const data = await API.get(`document/getDocumentDetails/${document_id}`);
    return data;
  } catch (error) {
    const status = error?.response?.status;
    if (status) {
      return { status, data: error?.response?.data ?? null };
    }
    console.log(error);
    return null;
  }
};

const uploadFile = async (state) => {
  const { formData } = state;

  try {
    const data = await API.post(`document/uploadfile`, formData);
    return data;
  } catch (error) {
    return [];
  }
};


const deleteFile = async (client_document_id, folder_name, file_name) => {
  // Use original GET endpoint; let interceptor add auth headers; encode path params
  const safeFolder = encodeURIComponent(folder_name || "");
  const safeFile = encodeURIComponent(file_name || "");

  try {
    const data = await API.get(
      `document/deletefile/${client_document_id}/${safeFolder}/${safeFile}`
    );
    if (data.status === 200) {
      Toaster.success("Document deleted successfully");
    } else {
      Toaster.error(
        data.data?.message || "Error occurred while deleting the document"
      );
    }
    return data;
  } catch (error) {
    console.log("DELETE ERROR RESPONSE", error);
    Toaster.error(
      error.response?.data?.message ||
        "Error occurred while deleting the document"
    );
    return [];
  }
};

const renameFile = async (formData) => {
  try {
    console.log("SENDING FORM DATA TO BE:", [...formData.entries()]);
    const response = await API.post(`document/renamefile`, formData, {
      headers: {
        "Content-Type": "multipart/form-data", // Ensure formData is recognized
      },
    });

    console.log("RENAME RESPONSE", response);
    if (response.status === 200) {
      Toaster.success("Document renamed successfully!");
    } else {
      Toaster.error("Error occurred while renaming the document.");
    }
    return response;
  } catch (error) {
    console.error("RENAME ERROR RESPONSE:", error);
    Toaster.error("Error occurred while renaming the document.");
    return [];
  }
};

const createFolder = async (state) => {
  try {
    // Remove custom headers - they're handled by the Axios interceptor
    // The interceptor automatically adds: x-access-token, user-type, user-id
    const data = await API.post(`folder/createFolder`, state);
    return data;
  } catch (error) {
    return [];
  }
};

// documentAction.js
export const renameFolder = createAsyncThunk(
  "document/renameFolder",
  async (payload, { rejectWithValue }) => {
    try {
      const { userId, folder_id, home_folder, folder_name, new_folder_name, oldFolderPath , newFolderPath } = payload;

      const { data } = await API.post("document/renamefolder", {
        userId,
        folder_id,
        home_folder,
        folder_name,
        new_folder_name,
        oldFolderPath,
        newFolderPath
      });

      return data.message;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


export const deleteFolder = createAsyncThunk(
  "document/deleteFolder",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { folderPath, home_folder, folder_name, userId, folder_id } = payload;
      const { data } = await API.delete("/document/deletefolder", {
        data: { folderPath, home_folder, folder_name, userId, client_folder_id: folder_id }, // Axios allows `data` in DELETE this way
      });
      dispatch(getUserFolders({ user_id: userId, folder_name: "", folder_id: "root" }));
      return data.message;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Move a folder (and all of its contents) to a new parent folder.
// Backend is expected to:
//   1. Validate destination (not self/descendant). Destination may differ in
//      doc_type/folder_type (sent/received/office) and document_category
//      (individual/business) from the source when new_* fields are sent.
//   2. Update parent_folder_id, folder_type, document_category,
//      directory_details and S3 prefix on the moved folder and recursively
//      on every descendant folder / document.
//   3. Copy all S3 objects under the old prefix to the new prefix and
//      delete the originals so the storage layout matches.
const moveFolder = async (payload) => {
  try {
    const response = await API.post(`folder/moveFolder`, payload);
    return response;
  } catch (error) {
    return {
      status: error?.response?.status || 500,
      data:
        error?.response?.data || {
          status: 0,
          message: error?.message || "Failed to move folder",
        },
    };
  }
};

// Move a single document/file to a different folder.
// Backend is expected to:
//   1. Validate destination folder for same user_id. Destination may differ in
//      doc_type and document_category from source (cross-tab / cross-category).
//   2. Update client_folder_id, doc_type, document_category, folder_name,
//      directory_details and document_link (S3 key).
//   3. Copy the S3 object to the new key, then delete the original.
const moveFile = async (payload) => {
  try {
    const response = await API.post(`document/movefile`, payload);
    return response;
  } catch (error) {
    return {
      status: error?.response?.status || 500,
      data:
        error?.response?.data || {
          status: 0,
          message: error?.message || "Failed to move file",
        },
    };
  }
};


// Signature APIs: DocuSign paths kept for compatibility; backend serves Adobe Acrobat Sign.
// Previous DocuSign-only wiring (reuse later):
//   document/docusign/* → docusign.controller
export const sendDocumentForSignature = async (payload) => {
  try {
    const response = await API.post(
      "document/acrobat-sign/send-envelope",
      payload,
    );
    return response.data;
  } catch (error) {
    console.error("Error sending for signature:", error);
    throw error;
  }
};

export const getClientSignatureEnvelopes = async ({
  client_id,
  document_category,
  client_business_id,
} = {}) => {
  try {
    const params = {};
    if (client_id != null && client_id !== "") params.client_id = client_id;
    if (document_category) params.document_category = document_category;
    if (client_business_id != null && client_business_id !== "") {
      params.client_business_id = client_business_id;
    }
    const response = await API.get("document/acrobat-sign/envelopes", {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching signature envelopes:", error);
    throw error;
  }
};

export const getSignatureEnvelopeStatus = async (envelopeId) => {
  try {
    const response = await API.get(
      `document/acrobat-sign/envelopes/${encodeURIComponent(envelopeId)}`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching envelope status:", error);
    throw error;
  }
};

export const resendSignatureEnvelope = async (envelopeId) => {
  try {
    const response = await API.post(
      `document/acrobat-sign/envelopes/${encodeURIComponent(envelopeId)}/resend`,
    );
    return response.data;
  } catch (error) {
    console.error("Error resending signature envelope:", error);
    throw error;
  }
};

export const voidSignatureEnvelope = async (envelopeId, reason = "") => {
  try {
    const response = await API.post(
      `document/acrobat-sign/envelopes/${encodeURIComponent(envelopeId)}/void`,
      { reason },
    );
    return response.data;
  } catch (error) {
    console.error("Error voiding signature envelope:", error);
    throw error;
  }
};

export const getSignatureSigningUrl = async (envelopeId) => {
  try {
    const response = await API.get(
      `document/acrobat-sign/envelopes/${encodeURIComponent(envelopeId)}/signing-url`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching SignWell signing URL:", error);
    throw error;
  }
};

export const getDocuSignAuthStatus = async () => {
  try {
    const response = await API.get("document/acrobat-sign/auth-status");
    return response.data;
  } catch (error) {
    console.error("Error fetching Acrobat Sign auth status:", error);
    throw error;
  }
};

const downloadViewFile = async (state) => {
  const { fileURL, document_name, document_type, client_document_id, type } = state;
  console.log("DOCUMENT DOWNLOAD ID: ",client_document_id);
  const data = await API({
    url: `document/viewfile`,
    method: "POST",
    data: { fileURL, client_document_id, type },
    responseType: "blob", // important
  });
  console.log("Download Response in fe: ",data);
  const RAW = data.data;
  if (type === "Preview") {
    const ext = document_name?.includes(".")
      ? document_name.split(".").pop()?.toLowerCase()
      : "";
    const mimeByExt = {
      pdf: "application/pdf",
      png: "image/png",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      gif: "image/gif",
      webp: "image/webp",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      xls: "application/vnd.ms-excel",
      csv: "text/csv",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      doc: "application/msword",
      txt: "text/plain",
    };
    const mime =
      document_type || (ext && mimeByExt[ext]) || "application/octet-stream";
    const blob =
      RAW instanceof Blob ? RAW : new Blob([RAW], { type: mime });
    return { ...data, raw: URL.createObjectURL(blob) };
  } else {
    let blob = new Blob([RAW], {
      type: document_type,
    });
    FileSaver.saveAs(blob, document_name);
  }
  return data;
};

const getBulkFiles = async (params) => {
  const data = await API({
    url: `document/bulkDownload`,
    method: "POST",
    data: params,
    responseType: "blob", // important
  });
  const RAW = data.data;

  let blob = new Blob([RAW], {
    type: "application/x-zip-compressed",
  });
  FileSaver.saveAs(blob, params.folder_name);
};

function withRequestedFileCategory(payload, category) {
  const cat = category
    ? String(category).trim().toLowerCase()
    : "";
  if (cat !== "individual" && cat !== "business") return payload;
  return {
    ...payload,
    requested_file_category: cat,
    document_category: cat,
  };
}

/** Staff requests a file name from a client; backend sends notification email. */
const requestFileFromClient = async ({
  client_id,
  file_name,
  requested_file_category,
  document_category,
  client_business_id,
  reminder_frequency_days,
  reminder_total_count,
}) => {
  try {
    const payload = { client_id, file_name };
    if (client_business_id != null && client_business_id !== "") {
      payload.client_business_id = client_business_id;
    }
    if (reminder_frequency_days != null && reminder_frequency_days !== "") {
      payload.reminder_frequency_days = Number(reminder_frequency_days);
    }
    if (reminder_total_count != null && reminder_total_count !== "") {
      payload.reminder_total_count = Number(reminder_total_count);
    }
    const { data } = await API.post(
      `document/requestFileFromClient`,
      withRequestedFileCategory(
        payload,
        requested_file_category || document_category,
      ),
    );
    return data;
  } catch (error) {
    return {
      status: 0,
      message:
        error.response?.data?.message || "Could not submit file request",
    };
  }
};

const requestFilesFromClientOneByOne = async ({
  client_id,
  file_names,
  requested_file_category,
  document_category,
  client_business_id,
  reminder_frequency_days,
  reminder_total_count,
}) => {
  const names = (file_names || [])
    .map((n) => String(n ?? "").trim())
    .filter(Boolean);
  if (!names.length) {
    return { status: 0, message: "No file names provided" };
  }

  const category = requested_file_category || document_category;
  let successCount = 0;
  let lastError = "Could not send file requests";

  for (let i = 0; i < names.length; i++) {
    const res = await requestFileFromClient({
      client_id,
      file_name: names[i],
      requested_file_category: category,
      client_business_id,
      reminder_frequency_days,
      reminder_total_count,
    });
    if (res && res.status === 1) {
      successCount += 1;
    } else if (res?.message) {
      lastError = res.message;
    }
  }

  if (successCount === names.length) {
    return {
      status: 1,
      message:
        successCount === 1
          ? "File request sent"
          : `${successCount} file requests sent`,
    };
  }
  if (successCount > 0) {
    return {
      status: 1,
      message: `${successCount} of ${names.length} file requests sent. ${lastError}`,
    };
  }
  return { status: 0, message: lastError };
};

/**
 * Request multiple file names from a client (one notification preferred).
 * Uses batch endpoint when available; otherwise calls requestFileFromClient per name.
 */
const listDocumentTemplates = async (category) => {
  try {
    const { data } = await API.get(`document/templates`, {
      params: { category },
    });
    return data;
  } catch (error) {
    return {
      status: 0,
      message: error.response?.data?.message || "Could not load templates",
      data: [],
    };
  }
};

const createDocumentTemplate = async ({ template_name, category, file }) => {
  try {
    const form = new FormData();
    form.append("template_name", template_name);
    form.append("category", category);
    form.append("file", file);
    const { data } = await API.post(`document/templates`, form);
    return data;
  } catch (error) {
    return {
      status: 0,
      message: error.response?.data?.message || "Could not save template",
    };
  }
};

const downloadDocumentTemplate = async ({
  template_id,
  name,
  preview = false,
  contentType,
}) => {
  const data = await API({
    url: "document/downloadTemplate",
    method: "POST",
    data: { template_id },
    responseType: "blob",
  });
  const raw = data.data;
  if (raw?.type && String(raw.type).includes("application/json")) {
    const text = await raw.text();
    let message = "Could not download template";
    try {
      message = JSON.parse(text)?.message || message;
    } catch (_) {
      /* keep default */
    }
    throw new Error(message);
  }
  const headerType = String(data.headers?.["content-type"] || "")
    .split(";")[0]
    .trim();
  const documentType =
    (headerType && !/octet-stream|json/i.test(headerType) && headerType) ||
    contentType ||
    headerType ||
    "application/octet-stream";
  const blob =
    raw instanceof Blob && raw.type && !/octet-stream|json/i.test(raw.type)
      ? raw
      : new Blob([raw], { type: documentType });
  if (preview) {
    return {
      blobUrl: URL.createObjectURL(blob),
      documentType,
      documentName: name || "template",
    };
  }
  FileSaver.saveAs(blob, name || "template");
};

const deleteDocumentTemplate = async (template_id) => {
  try {
    const { data } = await API.post(`document/deleteTemplate`, { template_id });
    return data;
  } catch (error) {
    return {
      status: 0,
      message: error.response?.data?.message || "Could not delete template",
    };
  }
};

const requestFilesFromClient = async ({
  client_id,
  file_names,
  attachments,
  template_ids,
  requested_file_category,
  document_category,
  client_business_id,
  reminder_frequency_days,
  reminder_total_count,
}) => {
  const names = (file_names || [])
    .map((n) => String(n ?? "").trim())
    .filter(Boolean);
  if (!names.length) {
    return { status: 0, message: "No file names provided" };
  }

  const category = requested_file_category || document_category || "individual";
  const attachmentLists = Array.isArray(attachments) ? attachments : [];
  const templateIds = Array.isArray(template_ids) ? template_ids : [];
  const hasAttachments = attachmentLists.some(
    (files) => Array.isArray(files) && files.length > 0,
  );
  const hasTemplates = templateIds.some((id) => String(id || "").trim());

  try {
    if (hasAttachments || hasTemplates) {
      const form = new FormData();
      form.append("client_id", client_id);
      form.append("file_names", JSON.stringify(names));
      if (client_business_id != null && client_business_id !== "") {
        form.append("client_business_id", client_business_id);
      }
      if (reminder_frequency_days != null && reminder_frequency_days !== "") {
        form.append("reminder_frequency_days", String(reminder_frequency_days));
      }
      if (reminder_total_count != null && reminder_total_count !== "") {
        form.append("reminder_total_count", String(reminder_total_count));
      }
      const categorized = withRequestedFileCategory({}, category);
      if (categorized.requested_file_category) {
        form.append("requested_file_category", categorized.requested_file_category);
        form.append("document_category", categorized.document_category);
      }
      form.append(
        "template_ids",
        JSON.stringify(names.map((_, index) => templateIds[index] || "")),
      );
      attachmentLists.forEach((files, index) => {
        (files || []).forEach((file) => {
          form.append(`attachment_${index}`, file);
        });
      });
      const { data } = await API.post(`document/requestFilesFromClient`, form);
      return data;
    }

    const payload = { client_id, file_names: names };
    if (client_business_id != null && client_business_id !== "") {
      payload.client_business_id = client_business_id;
    }
    if (reminder_frequency_days != null && reminder_frequency_days !== "") {
      payload.reminder_frequency_days = Number(reminder_frequency_days);
    }
    if (reminder_total_count != null && reminder_total_count !== "") {
      payload.reminder_total_count = Number(reminder_total_count);
    }
    const { data } = await API.post(
      `document/requestFilesFromClient`,
      withRequestedFileCategory(
        payload,
        category,
      ),
    );
    return data;
  } catch (error) {
    const httpStatus = error.response?.status;
    if (httpStatus === 404 || httpStatus === 405 || httpStatus === 501) {
      return requestFilesFromClientOneByOne({
        client_id,
        file_names: names,
        requested_file_category: category,
        client_business_id,
        reminder_frequency_days,
        reminder_total_count,
      });
    }
    return {
      status: 0,
      message:
        error.response?.data?.message || "Could not submit file requests",
    };
  }
};

/** List requested files for a client (staff or that client). */
const downloadRequestedFileAttachment = async ({
  requested_file_id,
  index,
  name,
  preview = false,
  contentType,
}) => {
  const data = await API({
    url: "document/downloadRequestedFileAttachment",
    method: "POST",
    data: { requested_file_id, index },
    responseType: "blob",
  });
  const raw = data.data;
  if (raw?.type && String(raw.type).includes("application/json")) {
    const text = await raw.text();
    let message = "Could not download document";
    try {
      message = JSON.parse(text)?.message || message;
    } catch (_) {
      /* keep default */
    }
    throw new Error(message);
  }
  const headerType = String(data.headers?.["content-type"] || "")
    .split(";")[0]
    .trim();
  const documentType =
    (headerType && !/octet-stream|json/i.test(headerType) && headerType) ||
    contentType ||
    headerType ||
    "application/octet-stream";
  const blob =
    raw instanceof Blob && raw.type && !/octet-stream|json/i.test(raw.type)
      ? raw
      : new Blob([raw], { type: documentType });
  if (preview) {
    return {
      blobUrl: URL.createObjectURL(blob),
      documentType,
      documentName: name || "document",
    };
  }
  FileSaver.saveAs(blob, name || "document");
};

const getRequestedFiles = async ({
  client_id,
  requested_file_category,
  document_category,
  client_business_id,
}) => {
  try {
    const category = requested_file_category || document_category;
    const body = { client_id };
    if (category) {
      const cat = String(category).trim().toLowerCase();
      body.requested_file_category = cat;
      body.document_category = cat;
    }
    if (client_business_id != null && client_business_id !== "") {
      body.client_business_id = client_business_id;
    }
    const { data } = await API.post(`document/getRequestedFiles`, body);
    if (data.status === 1) return Array.isArray(data.data) ? data.data : [];
    return [];
  } catch (error) {
    return [];
  }
};

/** Update status to pending | completed; backend sends email only on change (not initial create). */
const updateRequestedFileStatus = async ({ requested_file_id, status }) => {
  try {
    const { data } = await API.post(`document/updateRequestedFileStatus`, {
      requested_file_id,
      status,
    });
    return data;
  } catch (error) {
    return {
      status: 0,
      message:
        error.response?.data?.message || "Could not update status",
    };
  }
};

const updateRequestedFileDetails = async ({
  requested_file_id,
  file_name,
  reminder_frequency_days,
  reminder_total_count,
  reminders_sent_count,
  reminders_completed,
  next_reminder_at,
  reset_reminder_schedule,
  send_immediate_reminder,
}) => {
  try {
    const payload = buildRequestedFileDetailsPayload({
      requested_file_id,
      file_name,
      reminder_frequency_days,
      reminder_total_count,
      reminders_sent_count,
      reminders_completed,
      next_reminder_at,
      reset_reminder_schedule,
      send_immediate_reminder,
    });
    const { data } = await API.post(
      `document/updateRequestedFileDetails`,
      payload,
    );
    return data;
  } catch (error) {
    return {
      status: 0,
      message:
        error.response?.data?.message || "Could not update file request details",
    };
  }
};

function buildRequestedFileDetailsPayload(update = {}) {
  const payload = {
    requested_file_id: update.requested_file_id,
    file_name: update.file_name,
    reminder_frequency_days: update.reminder_frequency_days,
  };
  if (update.reminder_total_count != null && update.reminder_total_count !== "") {
    payload.reminder_total_count = Number(update.reminder_total_count);
  }
  if (update.reminders_sent_count != null && update.reminders_sent_count !== "") {
    payload.reminders_sent_count = Number(update.reminders_sent_count);
  }
  if (update.reminders_completed != null && update.reminders_completed !== "") {
    payload.reminders_completed = update.reminders_completed;
  }
  if (update.next_reminder_at != null && update.next_reminder_at !== "") {
    payload.next_reminder_at = update.next_reminder_at;
  }
  if (update.reset_reminder_schedule != null) {
    payload.reset_reminder_schedule = Boolean(update.reset_reminder_schedule);
  }
  if (update.send_immediate_reminder != null) {
    payload.send_immediate_reminder = Boolean(update.send_immediate_reminder);
  }
  return payload;
}

const bulkUpdateRequestedFileDetailsOneByOne = async (updates = []) => {
  let successCount = 0;
  let lastError = "Could not update requested files";
  const results = [];

  for (const update of updates) {
    const res = await updateRequestedFileDetails(update);
    results.push({ update, res });
    if (res && res.status === 1) {
      successCount += 1;
    } else if (res?.message) {
      lastError = res.message;
    }
  }

  if (successCount === updates.length) {
    return {
      status: 1,
      message: `Updated ${successCount} file(s)`,
      data: { updated_count: successCount, results },
    };
  }
  if (successCount > 0) {
    return {
      status: 0,
      message: `${successCount} of ${updates.length} updated. ${lastError}`,
      data: { updated_count: successCount, results },
    };
  }
  return {
    status: 0,
    message: lastError,
    data: { updated_count: 0, results },
  };
};

/** Bulk update requested file details in one API call (falls back to per-file updates). */
const bulkUpdateRequestedFileDetails = async ({ updates = [] } = {}) => {
  const normalized = (updates || [])
    .map((update) => {
      if (!update?.requested_file_id) return null;
      return buildRequestedFileDetailsPayload(update);
    })
    .filter(Boolean);

  if (!normalized.length) {
    return { status: 0, message: "No requested files selected" };
  }

  try {
    const { data } = await API.post(`document/bulkUpdateRequestedFileDetails`, {
      updates: normalized,
    });
    return data;
  } catch (error) {
    const httpStatus = error.response?.status;
    if (httpStatus === 404 || httpStatus === 405 || httpStatus === 501) {
      return bulkUpdateRequestedFileDetailsOneByOne(normalized);
    }
    return {
      status: 0,
      message:
        error.response?.data?.message || "Could not update requested files",
    };
  }
};

/** Link uploaded document to a request (if upload endpoint does not do it in one step). */
const completeRequestedFileWithUpload = async ({
  requested_file_id,
  client_document_id,
}) => {
  try {
    const { data } = await API.post(`document/completeRequestedFileWithUpload`, {
      requested_file_id,
      client_document_id,
    });
    return data;
  } catch (error) {
    return {
      status: 0,
      message:
        error.response?.data?.message || "Could not link upload to request",
    };
  }
};

/** Tab badge totals + per-folder maps for document notifications. */
const getDocumentTabUnreadCounts = async ({
  client_id,
  document_category,
  client_business_id,
} = {}) => {
  if (isTabUnreadCountsApiUnavailable()) {
    return { unavailable: true, status: 404 };
  }

  try {
    const body = {};
    if (client_id != null && client_id !== "") {
      body.client_id = client_id;
    }
    if (document_category) {
      body.document_category = document_category;
    }
    if (client_business_id != null && client_business_id !== "") {
      body.client_business_id = client_business_id;
    }
    const { data } = await API.post(
      `document/getDocumentTabUnreadCounts`,
      body,
    );
    if (data?.status === 1 || data?.data) {
      return data;
    }
    return null;
  } catch (error) {
    if (isEndpointNotImplementedError(error)) {
      markTabUnreadCountsApiUnavailable();
      return { unavailable: true, status: error?.response?.status || 404 };
    }
    console.log(error);
    return null;
  }
};

export {
  getUserFiles,
  getUserFolders,
  getAllClientDocuments,
  getFolderDetails,
  getDocumentDetails,
  uploadFile,
  deleteFile,
  createFolder,
  downloadViewFile,
  getBulkFiles,
  renameFile,
  requestFileFromClient,
  requestFilesFromClient,
  listDocumentTemplates,
  createDocumentTemplate,
  downloadDocumentTemplate,
  deleteDocumentTemplate,
  downloadRequestedFileAttachment,
  getRequestedFiles,
  updateRequestedFileStatus,
  completeRequestedFileWithUpload,
  getDocumentTabUnreadCounts,
  moveFolder,
  moveFile,
  updateRequestedFileDetails,
  bulkUpdateRequestedFileDetails,
};
